import Logo from '../../assets/navbaricon.png'
import { useEffect, useState, useRef, useCallback } from 'react'
import profile_logo from '../../assets/profile-logo.png'
import { useAuth } from '../../context/logincontext'
import { useNavigate, useLocation } from 'react-router-dom'
import validateJSON from '../../util/jsonvalidation'
import IonIcon from '@reacticons/ionicons'
import TooltipWalkthrough from '../../util/walkthrough'
import shopifyLogo from '../../assets/shopifybag.png'
import websiteLogo from '../../assets/webicon.png'
import magentoLogo from '../../assets/magento.png'
import { getStore, getusersubscription } from '../../services/data'
import { useSubscription } from '../../context/subscriptionprovider'
import { useStore } from '../../context/storecontext'
import { useStoreId } from '../../hooks/geturlstoreid'

const Navbar = () => {
  const { userdata, logout } = useAuth()
  const { storeId, setStoreId, storeData, setStoreData } = useStore()
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(() => userdata)
  const [homeVisible, setHomeVisible] = useState(true)
  const [forceRun, setForceRun] = useState(false)
  const dropdownRef = useRef(null)
  const [storeList, setStoreList] = useState({})
  const { refreshSubscription } = useSubscription()
  const [showMobileDialog, setShowMobileDialog] = useState(false)
  const [currentStoreLogoImg, setCurrentStoreLogoImg] = useState(shopifyLogo)
  const [urlStoreId, setUrlStoreId] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const storeIdFromUrl = useStoreId()

  // Extract store ID from current URL path
  useEffect(() => {
    const pathParts = location.pathname.split('/')
    if (pathParts.length >= 3 && pathParts[1] === 'store') {
      setUrlStoreId(pathParts[2])
    } else {
      setUrlStoreId('')
    }
  }, [location.pathname])

  // Prioritize store ID: URL > Context > LocalStorage
  const effectiveStoreId = urlStoreId || storeId || storeData?.store_id || ''

  const platforms = [
    { name: 'Shopify', logo: shopifyLogo, key: 'shopify' },
    { name: 'Website', logo: websiteLogo, key: 'website' },
    { name: 'Magento', logo: magentoLogo, key: 'magento' }
  ]

  // Helper function to get logo for a store
  const getStoreLogo = useCallback(
    storeType => {
      if (!storeType) return shopifyLogo

      let platform = platforms.find(p => p.key === storeType.toLowerCase())

      if (!platform) {
        platform = platforms.find(
          p => p.name.toLowerCase() === storeType.toLowerCase()
        )
      }

      return platform ? platform.logo : shopifyLogo
    },
    [platforms]
  )

  // Get current store logo
  const getCurrentStoreLogo = useCallback(() => {
    if (storeData?.type) {
      return getStoreLogo(storeData.type)
    }

    const storedLogo = localStorage.getItem('store_logo')
    if (storedLogo) {
      return storedLogo
    }

    const storedPlatform = localStorage.getItem('store_platform')
    if (storedPlatform) {
      return getStoreLogo(storedPlatform)
    }

    return shopifyLogo
  }, [storeData, getStoreLogo])

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      const result = {}
      for (const platform of platforms) {
        try {
          const res = await getStore(platform.name.toLowerCase(), 'active')
          if (res.success && Array.isArray(res.data)) {
            result[platform.name] = res.data.map(store => ({
              ...store,
              logo: platform.logo
            }))
          }
        } catch (err) {
          console.error(`Error fetching stores for ${platform.name}`, err)
        }
      }
      setStoreList(result)
    }
    fetchStores()
  }, [])

  // Initialize store data from URL or localStorage
  useEffect(() => {
    const initializeStoreData = async () => {
      // If we have a store ID from URL but no storeData in context
      if (urlStoreId && (!storeData || storeData.store_id !== urlStoreId)) {
        try {
          // Try to get store data from localStorage first
          const storedData = localStorage.getItem('storedata')
          if (storedData) {
            const parsedData = JSON.parse(storedData)
            if (parsedData.store_id === urlStoreId) {
              setStoreData(parsedData)
              setStoreId(parsedData.store_id)
              setCurrentStoreLogoImg(getStoreLogo(parsedData.type))
              return
            }
          }

          setStoreId(urlStoreId)
        } catch (error) {
          console.error('Error initializing store data from URL:', error)
        }
      }

      if (!urlStoreId && !storeData) {
        try {
          const storedData = localStorage.getItem('storedata')
          if (storedData) {
            const parsedData = JSON.parse(storedData)
            setStoreData(parsedData)
            setStoreId(parsedData.store_id)
            setCurrentStoreLogoImg(getStoreLogo(parsedData.type))
          }
        } catch (error) {
          console.error(
            'Error initializing store data from localStorage:',
            error
          )
        }
      }
    }

    initializeStoreData()
  }, [urlStoreId, storeData, setStoreData, setStoreId, getStoreLogo])

  // Fixed store change logic
  const storeChange = useCallback(
    async store => {
      if (!store) return

      setStoreId(store.store_id)
      setStoreData(store)

      const storeLogo = getStoreLogo(store.type)

      localStorage.setItem('store_platform', store.type)
      localStorage.setItem('store_logo', storeLogo)
      localStorage.setItem('storedata', JSON.stringify(store))
      localStorage.setItem('chatbot', store.type)

      window.dispatchEvent(
        new CustomEvent('localStorageChange', {
          detail: { key: 'storedata', value: store }
        })
      )

      setCurrentStoreLogoImg(storeLogo)
      setActiveDropdown(null)

      if (location.pathname === '/home') {
        try {
          const res = await getusersubscription(
            '',
            user?.id ?? '',
            '',
            store.store_id,
            'active',
            ''
          )
          const data = res?.data
          const isActive =
            (Array.isArray(data) && data[0]?.status === 'active') ||
            (!Array.isArray(data) && data?.status === 'active')

          if (isActive) {
            refreshSubscription()
            navigate(`/store/${store.store_id}/training`)
          } else {
            refreshSubscription()
            navigate(`/store/${store.store_id}/subscription`)
          }
        } catch (error) {
          console.error('Error fetching subscription:', error)
          navigate(`/store/${store.store_id}/subscription`)
        }
      } else {
        const currentPath = location.pathname

        if (currentPath.startsWith('/store/')) {
          const pathParts = currentPath.split('/')
          const remainingPath = pathParts.slice(3).join('/')
          navigate(`/store/${store.store_id}/${remainingPath}`)
          window.location.reload()
        } else {
          navigate(`/store/${store.store_id}/training`)
        }
      }
    },
    [
      setStoreId,
      setStoreData,
      getStoreLogo,
      location.pathname,
      user?.id,
      refreshSubscription,
      navigate
    ]
  )

  // Watch for localStorage changes and custom events
  useEffect(() => {
    const handleStorageChange = e => {
      if (e.key === 'storedata') {
        updateStoreFromStorage()
      }
    }

    const handleCustomStorageChange = e => {
      if (e.detail.key === 'storedata') {
        setStoreData(e.detail.value)
        setStoreId(e.detail.value.store_id)
        setCurrentStoreLogoImg(getStoreLogo(e.detail.value.type))
      }
    }

    const updateStoreFromStorage = () => {
      try {
        const storedData = localStorage.getItem('storedata')
        if (storedData) {
          const newStoreData = JSON.parse(storedData)
          if (newStoreData.store_id !== storeData?.store_id) {
            setStoreData(newStoreData)
            setStoreId(newStoreData.store_id)
            setCurrentStoreLogoImg(getStoreLogo(newStoreData.type))
          }
        }
      } catch (error) {
        console.error('Error updating store from storage:', error)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('localStorageChange', handleCustomStorageChange)

    // Initial load from localStorage
    updateStoreFromStorage()

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'localStorageChange',
        handleCustomStorageChange
      )
    }
  }, [storeData, setStoreData, setStoreId, getStoreLogo])

  // Update current store logo when storeData changes
  useEffect(() => {
    const logo = getCurrentStoreLogo()
    setCurrentStoreLogoImg(logo)
  }, [storeData, storeId, getCurrentStoreLogo])

  const toggleDropdown = dropdownName => {
    setActiveDropdown(prev => (prev === dropdownName ? null : dropdownName))
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev)
  }

  const handleTriggerTutorialWalkthrough = () => {
    setIsMobileMenuOpen(false)
    const isMobile = window.innerWidth <= 900
    if (isMobile) {
      setShowMobileDialog(true)
      return
    }
    setShowMobileDialog(false)
    window.dispatchEvent(
      new CustomEvent('triggertutorialwalkthrough', { detail: true })
    )
    window.dispatchEvent(new CustomEvent('scrollLimit', { detail: true }))
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const isActive = path => location.pathname === path

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const switchAccount = localStorage.getItem('owner_account')
    if (switchAccount !== null) {
      setHomeVisible(switchAccount === 'true')
    }
  }, [])

  useEffect(() => {
    const savedUser = validateJSON(userdata)
    if (savedUser) setUser(savedUser)
  }, [userdata])

  // Build nav items with current storeId
  const navItems = [
    { path: '/home', name: 'Home', visible: homeVisible },
    { path: `/store/${effectiveStoreId}/training`, name: 'Training' },
    { path: `/store/${effectiveStoreId}/chats`, name: 'Chats' },
    {
      path: `/store/${effectiveStoreId}/bot-customization`,
      name: 'Bot Customization'
    },
    {
      path: `/store/${effectiveStoreId}/ticket-management`,
      name: 'Ticket Management'
    },
    {
      path: `/store/${effectiveStoreId}/analytics`,
      name: 'Analytics'
    },
    {
      path: `/store/${effectiveStoreId}/subscription`,
      name: 'Subscription'
    },
    { path: `/store/${effectiveStoreId}/feedback`, name: 'Support' }
  ]

  const enableWalkthrough = location.pathname === '/subscription'

  // Store dropdown for desktop
  const renderStoreDropdown = () => {
    const allStores = Object.entries(storeList).flatMap(([platform, stores]) =>
      stores.map(store => ({
        ...store,
        platform,
        logo: getStoreLogo(store.type)
      }))
    )

    const currentStoreId = effectiveStoreId

    const sortedStores = allStores.sort((a, b) =>
      a.store_id === currentStoreId ? -1 : b.store_id === currentStoreId ? 1 : 0
    )

    return (
      <div className='absolute top-[50px] right-0 bg-white shadow-lg rounded-xl min-w-[280px] max-w-[500px] max-h-[500px] overflow-y-auto z-[10000]'>
        <ul className='divide-y divide-gray-200'>
          {sortedStores.map(store => {
            const isSelected = store.store_id === currentStoreId

            return (
              <li
                key={store.store_id}
                className={`px-4 py-2 flex items-center gap-3 cursor-pointer text-sm ${
                  isSelected
                    ? 'bg-[#7C50DD] bg-opacity-10 text-[#7C50DD] font-semibold'
                    : 'hover:bg-gray-100 text-gray-800'
                }`}
                onClick={() => storeChange(store)}
              >
                <img src={store.logo} alt='icon' className='w-5 h-5' />
                <span className='truncate'>{store.name}</span>
                {isSelected && (
                  <IonIcon
                    name='checkmark'
                    className='text-[#7C50DD] ml-auto'
                  />
                )}
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <>
      {/* Feature not available dialog */}
      {showMobileDialog && (
        <div className='fixed inset-0 z-[9999] bg-black bg-opacity-40 flex items-center justify-center'>
          <div className='bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-sm text-center'>
            <h2 className='text-lg font-semibold mb-2 text-gray-800'>
              Feature Not Available
            </h2>
            <p className='text-sm text-gray-600 mb-4'>
              This feature is only available on desktop mode.
            </p>
            <button
              onClick={() => setShowMobileDialog(false)}
              className='bg-[#7C50DD] text-white px-4 py-2 rounded-md w-full'
            >
              Okay
            </button>
          </div>
        </div>
      )}

      <div className='bg-[#232328] w-full h-[59px] flex items-center text-[#FFFFFF] justify-between p-3 rounded-[9px]'>
        {/* Logo Desktop */}
        <div className='hidden lg:flex'>
          <img src={Logo} alt='logo' className='h-[27px]' />
        </div>

        {/* Hamburger for Mobile */}
        <div className='flex items-center justify-between w-full lg:hidden'>
          <button className='text-white' onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? '✖' : '☰'}
          </button>
          <img src={Logo} alt='logo' className='h-[27px]' />
          <div className='w-6 h-6'></div>
        </div>

        {/* Navigation Desktop */}
        <div className='hidden lg:flex justify-between w-full lg:w-auto'>
          <ul className='flex items-center space-x-7 px-2 cursor-pointer'>
            {navItems.map(
              item =>
                item.visible !== false && (
                  <li key={item.path} onClick={() => navigate(item.path)}>
                    <p
                      className={`capitalize font-bold text-[11.28px] text-white ${
                        isActive(item.path)
                          ? 'text-opacity-100'
                          : 'text-opacity-60'
                      } ${location.pathname === '/home' ? 'hidden' : ''}`}
                    >
                      {item.name}
                    </p>
                  </li>
                )
            )}
          </ul>
        </div>

        {/* Profile + Store Dropdown */}
        <div className='flex items-center gap-2' ref={dropdownRef}>
          {/* Walkthrough */}
          {![
            `/store/${storeId}/feedback`,
            `/store/${storeId}/subscription`,
            `/store/${storeId}/analytics`
          ].includes(location.pathname) && (
            <IonIcon
              data-tooltip-id='my-tooltip'
              data-tooltip-content='Take a Tour'
              name='play-circle-outline'
              className='text-[22px] cursor-pointer'
              onClick={handleTriggerTutorialWalkthrough}
            />
          )}

          {/* Store Dropdown */}
          <div className='relative hidden lg:block'>
            <div
              className='bg-white h-[45px] w-[75px] rounded-[8px] flex items-center justify-between px-3 cursor-pointer shadow-sm'
              onClick={() => toggleDropdown('store')}
            >
              <img
                src={currentStoreLogoImg}
                alt={storeData?.type || 'Store'}
                className='w-6 h-6'
              />
              <svg width='18' height='19' viewBox='0 0 18 19'>
                <path
                  d='M3.445 7.18L9 13.21l5.555-6.03H3.445z'
                  fill='#565656'
                />
              </svg>
            </div>
            {activeDropdown === 'store' && renderStoreDropdown()}
          </div>

          {/* Profile Dropdown */}
          <div className='relative hidden lg:block'>
            <div
              className='bg-white h-[45px] w-[75px] rounded-[8px] flex items-center justify-between px-3 cursor-pointer shadow-sm'
              onClick={() => toggleDropdown('profile')}
            >
              <img
                src={profile_logo}
                alt='Profile'
                className='w-6 h-6 rounded-full'
              />
              <svg width='18' height='19' viewBox='0 0 18 19'>
                <path
                  d='M3.445 7.18L9 13.21l5.555-6.03H3.445z'
                  fill='#565656'
                />
              </svg>
            </div>
            {activeDropdown === 'profile' && (
              <div className='absolute top-[55px] right-0 bg-white shadow-md rounded-[8px] min-w-[290px] z-[10001]'>
                <ul className='py-2'>
                  <li className='px-4 py-2 flex items-center gap-3 hover:bg-gray-100'>
                    <img
                      src={profile_logo}
                      alt='Profile'
                      className='w-6 h-6 rounded-full'
                    />
                    <div>
                      <p className='text-sm font-medium text-gray-800'>
                        {user?.name || userdata.name}
                      </p>
                      <p className='text-xs text-gray-500'>
                        {user?.email || userdata.email}
                      </p>
                    </div>
                  </li>
                  <li
                    onClick={handleLogout}
                    className='px-4 py-2 border-t border-gray-200 cursor-pointer hover:bg-gray-100 text-gray-800'
                  >
                    Logout
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className='lg:hidden absolute bg-[#232328] rounded-md z-[1000] p-4 m-[10px] mx-[4%] left-0 right-0'>
          <ul className='flex flex-col space-y-4 cursor-pointer'>
            {navItems.map(
              item =>
                item.visible !== false && (
                  <li
                    key={item.path}
                    onClick={() => {
                      navigate(item.path)
                      setIsMobileMenuOpen(false)
                    }}
                  >
                    <p className='capitalize font-bold text-white'>
                      {item.name}
                    </p>
                  </li>
                )
            )}

            {/* Mobile Store Changer */}
            <li className='mt-4'>
              <p className='text-white font-semibold mb-2'>Select Store</p>
              <ul className='bg-white rounded-md p-2  max-h-48 overflow-y-auto'>
                {Object.entries(storeList)
                  .flatMap(([platform, stores]) =>
                    stores.map(store => ({
                      ...store,
                      platform,
                      logo: getStoreLogo(store.type)
                    }))
                  )
                  .map(store => (
                    <li
                      key={store.store_id}
                      className='flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-100 rounded'
                      onClick={() => {
                        storeChange(store)
                        setIsMobileMenuOpen(false)
                      }}
                    >
                      <img src={store.logo} alt='icon' className='w-5 h-5' />
                      <span className='text-gray-800 truncate'>
                        {store.name}
                      </span>
                    </li>
                  ))}
              </ul>
            </li>

            {/* User Info */}
            <li className='mt-4 flex items-center gap-3 bg-white p-2 rounded-md'>
              <img
                src={profile_logo}
                alt='Profile'
                className='w-10 h-10 rounded-full'
              />
              <div className='flex flex-col'>
                <span className='text-gray-800 font-medium'>
                  {user?.name || userdata.name}
                </span>
                <span className='text-gray-500 text-sm'>
                  {user?.email || userdata.email}
                </span>
              </div>
            </li>

            <li className='mt-4 text-white' onClick={handleLogout}>
              Logout
            </li>
          </ul>
        </div>
      )}

      {enableWalkthrough && <TooltipWalkthrough forceRun={forceRun} />}
    </>
  )
}

export default Navbar
